/**
 * MobileFixedHeader Component
 *
 * Fixed header for mobile views that includes:
 * - Tab title and icon
 * - Campaign and active route selectors
 * - Auto-collapsed description
 *
 * This header stays fixed at the top when scrolling on mobile devices.
 */

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, LucideIcon } from 'lucide-react';
import { TabHeaderSelector } from './TabHeaderSelector';
import { ActiveRouteIndicator } from './ActiveRouteIndicator';
import { useIsMobile } from '@shared/hooks';

export interface MobileFixedHeaderProps {
  /**
   * Page icon (Lucide icon component)
   */
  icon: LucideIcon;

  /**
   * Page title
   */
  title: string;

  /**
   * Page description (collapsible, auto-collapsed on mobile)
   */
  description?: string | React.ReactNode;

  /**
   * Unique key for localStorage (to persist collapse state)
   */
  storageKey?: string;

  /**
   * Whether to show campaign selector
   * Default: true (most tabs are campaign-dependent)
   */
  showCampaignSelector?: boolean;

  /**
   * Whether to show route selector
   * Default: true (available from all tabs)
   */
  showRouteSelector?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Mobile Fixed Header - Fixed-position header for mobile views
 */
export const MobileFixedHeader: React.FC<MobileFixedHeaderProps> = ({
  icon: Icon,
  title,
  description,
  storageKey,
  showCampaignSelector = true,
  showRouteSelector = true,
  className = '',
}) => {
  const isMobile = useIsMobile();

  // Auto-collapsed on mobile by default
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle icon click - opens mobile sidebar
  const handleIconClick = () => {
    // Dispatch event to open AppShell's mobile sidebar
    window.dispatchEvent(new CustomEvent('open-mobile-sidebar'));
  };

  // Always collapse on mount for mobile
  useEffect(() => {
    if (isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  // Toggle expand/collapse
  const toggleExpanded = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (storageKey) {
      localStorage.setItem(`mobile-header-${storageKey}`, String(newState));
    }
  };

  // Don't render on desktop
  if (!isMobile) {
    return null;
  }

  return (
    <div className={`
      fixed top-0 left-0 right-0 z-40
      bg-dark-surface border-b border-dark-border/30
      shadow-lg
      ${className}
    `}>
      {/* Main Header Content */}
      <div className="p-3">
        {/* Title Row */}
        <div className="flex items-center gap-3">
          {/* Icon - Clickable to open navigation sidebar */}
          <button
            onClick={handleIconClick}
            className="
              flex-shrink-0 w-10 h-10
              rounded-lg bg-brand-500/10 border border-brand-500/20
              flex items-center justify-center
              relative
              transition-all duration-200
              active:scale-95 active:bg-brand-500/20
              group
            "
            aria-label="Open navigation menu"
            data-testid="mobile-menu-button"
          >
            <Icon className="w-5 h-5 text-brand-500" />
            {/* Click indicator */}
            <div className="
              absolute -top-0.5 -right-0.5
              w-3.5 h-3.5
              bg-brand-600 rounded-full
              flex items-center justify-center
              shadow-sm
              group-active:bg-brand-500
            ">
              <ChevronDown className="w-2 h-2 text-white" />
            </div>
          </button>

          {/* Title and Expand Button */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-lg font-semibold text-white whitespace-nowrap truncate">
                {title}
              </h1>

              {/* Expand/Collapse button - always show if there's content to expand */}
              {(description || showCampaignSelector || showRouteSelector) && (
                <button
                  onClick={toggleExpanded}
                  className="
                    flex-shrink-0
                    w-8 h-8
                    rounded-lg
                    border border-dark-border/30
                    bg-dark-bg
                    hover:bg-dark-surface/50
                    active:bg-dark-surface/60
                    flex items-center justify-center
                    transition-all duration-150
                  "
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-dark-muted" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-dark-muted" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded Section - Campaign/Route Selectors and Description */}
        {isExpanded && (
          <div className="
            mt-3 pt-3 border-t border-dark-border/30
            animate-in slide-in-from-top-2 fade-in duration-200
          ">
            {/* Campaign and Route Selectors */}
            {showCampaignSelector && (
              <div>
                <TabHeaderSelector
                  showRouteSelector={showRouteSelector}
                  tabName={title}
                  compact={true}
                />
              </div>
            )}

            {/* Active Route Indicator */}
            {showRouteSelector && !showCampaignSelector && (
              <div>
                <ActiveRouteIndicator className="justify-start text-xs" showSeparator={false} />
              </div>
            )}

            {/* Description */}
            {description && (
              <div className={`text-xs text-dark-muted ${(showCampaignSelector || showRouteSelector) ? 'mt-3' : ''}`}>
                {typeof description === 'string' ? (
                  <p>{description}</p>
                ) : (
                  description
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileFixedHeader;